import React, { useState } from 'react'
import { AlertCircle, CheckCircle, AlertTriangle, TrendingUp, FileDown } from 'lucide-react'
import { Bar, Line } from 'react-chartjs-2'
import html2canvas from 'html2canvas'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js'
import { toDateSafe, toLocaleDateStringSafe } from '../utils/fechasSeguras'
import AlertaLegal from './AlertaLegal'
import TablaComparativa from './TablaComparativa'
import { generarReporteHTML } from '../utils/reporteHTML'
import TicketCreator from './TicketCreator'
import { BarChart, Bar as RechartsBar, Cell, CartesianGrid, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, ReferenceLine } from 'recharts'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
)

export default function DashboardResultados({ resultado, historiaLaboral, coinsConsumed, auditoriaRows = [], titulo = 'Auditoria Tecnica' }) {
  if (Array.isArray(auditoriaRows) && auditoriaRows.length > 0) {
    const filas = auditoriaRows.map((row, idx) => {
      const periodo = row.periodo || row.periodoISO || row.periodoKey || `Fila ${idx + 1}`
      const sueldoOriginal = Number(row.montoOriginal || row.monto || row.remImpSS || 0)
      const topeAplicado = Number(row.montoSujetoAAporte || sueldoOriginal)
      const coeficiente = Number(row.coeficiente || 1)
      const sueldoActualizado = Number(row.montoActualizado || topeAplicado)
      const recortadoPorTope = String(row.fuenteNormativa) === 'tope_base_imponible' && topeAplicado < sueldoOriginal
      const excluidoPorCese = Boolean(row.invalidoPorCese || row.motivoExclusion === 'Excluido por Cese')
      const periodoMatch = String(periodo).match(/^(\d{4})-(\d{2})$|^(\d{2})\/(\d{4})$/)
      let periodoNumeric = 0
      if (periodoMatch) {
        if (periodo.includes('/')) {
          const [mm, yyyy] = periodo.split('/')
          periodoNumeric = Number(`${yyyy}${mm}`)
        } else {
          const [yyyy, mm] = periodo.split('-')
          periodoNumeric = Number(`${yyyy}${mm}`)
        }
      }
      const monedaOriginal = String(row.moneda || '').toLowerCase()
      const requiereTrazaMoneda = periodoNumeric > 0 && periodoNumeric < 199104
      const tooltipMoneda = monedaOriginal.includes('austral')
        ? 'Monto original en Australes'
        : monedaOriginal.includes('peso_ley')
          ? 'Monto original en Pesos Ley'
          : 'Monto original en moneda historica previa a convertibilidad'
      return {
        id: `${periodo}-${idx}`,
        periodo,
        sueldoOriginal,
        topeAplicado,
        coeficiente,
        sueldoActualizado,
        recortadoPorTope,
        excluidoPorCese,
        requiereTrazaMoneda,
        tooltipMoneda,
        acumuladoConInteres: Number(row.acumuladoConInteres || 0),
        indiceBaseValor: Number(row.indiceBaseValor || 0),
        indiceCeseValor: Number(row.indiceCeseValor || 0),
        missingIndice: Boolean(row.missingIndice),
        alertaIndice: row.alertaIndice || '',
        missingInSource: Boolean(row.missingInSource),
      }
    })

    const mesesRecortados = filas.filter((f) => f.recortadoPorTope).length
    const retroactivoEstimado = filas.reduce((acc, f) => acc + Number(f.acumuladoConInteres || 0), 0)
    const fuenteInteres = auditoriaRows[0]?.tasaInteresFuente || 'BCRA - Serie Historica Com. 14.290'
    const fechaCorte = auditoriaRows[0]?.tasaInteresFechaCorte || new Date().toLocaleDateString('es-AR')
    const gapsCount = auditoriaRows.filter((r) => r.requiereRevision).length
    const gapsPct = auditoriaRows.length > 0 ? (gapsCount / auditoriaRows.length) * 100 : 0
    const baseRows = auditoriaRows.filter((r) => r.incluirEnPromedio !== false)
    const coefPlanos = baseRows.filter((r) => Number(r.coeficiente || 1) === 1).length
    const coefPlanoPct = baseRows.length > 0 ? (coefPlanos / baseRows.length) * 100 : 0
    const missingIndices = filas.filter((f) => f.missingIndice && f.sueldoOriginal > 0 && !f.missingInSource)
    const confiscatoriedadData = filas
      .filter((f) => f.recortadoPorTope && f.sueldoOriginal > 0)
      .map((f) => {
        const quitaPct = ((f.sueldoOriginal - f.topeAplicado) / f.sueldoOriginal) * 100
        return {
          periodo: f.periodo,
          quitaPct: Number(quitaPct.toFixed(2)),
        }
      })

    return (
      <section className="rounded-2xl border border-white/20 bg-white/10 p-4 shadow-[0_20px_50px_rgba(0,0,0,0.25)] backdrop-blur-2xl">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-base font-semibold text-slate-100">{titulo}</h3>
          <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${mesesRecortados > 0 ? 'bg-amber-500/20 text-amber-100 border border-amber-300/40' : 'bg-emerald-500/20 text-emerald-100 border border-emerald-300/40'}`}>
            Semaforo Juridico: {mesesRecortados > 0 ? `NARANJA (${mesesRecortados} meses con tope)` : 'VERDE (sin recortes por tope)'}
          </span>
        </div>

        <div className="mb-3 rounded-xl border border-cyan-300/30 bg-cyan-500/15 p-3">
          <p className="text-xs uppercase tracking-wide text-cyan-100">Retroactivo Estimado</p>
          <p className="mt-1 text-xl font-semibold text-cyan-50">${Math.round(retroactivoEstimado).toLocaleString('es-AR')}</p>
          <p className="mt-1 text-xs text-cyan-100/80">Sumatoria de diferencias mensuales proyectadas con interes a hoy.</p>
          <p className="mt-1 text-[11px] text-cyan-100/80">Fuente: {fuenteInteres}</p>
          <p className="text-[11px] text-cyan-100/80">Fecha de Corte de la Tasa: {fechaCorte}</p>
        </div>

        {gapsPct > 10 && (
          <div className="mb-3 rounded-xl border border-amber-300/40 bg-amber-500/20 p-3 text-amber-100">
            <p className="text-xs font-semibold uppercase tracking-wide">ALERTA</p>
            <p className="mt-1 text-sm">ALERTA: Se detectó una alta fragmentación de aportes. Verifique si existen períodos de moratoria o servicios no reconocidos que puedan elevar el Haber Inicial.</p>
          </div>
        )}

        {coefPlanoPct > 20 && (
          <div className="mb-3 rounded-xl border border-rose-300/40 bg-rose-500/20 p-3 text-rose-100">
            <p className="text-xs font-semibold uppercase tracking-wide">RIESGO DE IMPUGNACION</p>
            <p className="mt-1 text-sm">Se detectó un tramo relevante con coeficiente 1.000000 en la base de promedio. Revise empalme de índices para evitar objeciones de ANSES.</p>
          </div>
        )}

        {missingIndices.length > 0 && (
          <div className="mb-3 rounded-xl border border-rose-300/40 bg-rose-500/20 p-3 text-rose-100">
            <p className="text-xs font-semibold uppercase tracking-wide">ALERTA DE ÍNDICES</p>
            <p className="mt-1 text-sm">Falta dato de índice para {missingIndices.length} períodos. Revise la serie normativa antes de presentar.</p>
          </div>
        )}

        {confiscatoriedadData.length > 0 && (
          <div className="mb-3 rounded-xl border border-rose-300/30 bg-rose-500/10 p-3">
            <p className="mb-2 text-xs uppercase tracking-wide text-rose-100">Gráfico de Confiscatoriedad</p>
            <div className="h-64 min-h-[256px] w-full min-w-0">
              <ResponsiveContainer width="100%" height={256} minWidth={240} minHeight={220}>
                <BarChart data={confiscatoriedadData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.18)" />
                  <XAxis dataKey="periodo" stroke="#e2e8f0" tick={{ fontSize: 11 }} />
                  <YAxis stroke="#e2e8f0" tick={{ fontSize: 11 }} unit="%" />
                  <RechartsTooltip formatter={(v) => [`${v}%`, 'Quita por tope']} />
                  <ReferenceLine y={15} stroke="#f59e0b" strokeWidth={2} strokeDasharray="6 4" label={{ value: 'Límite 15%', position: 'insideTopRight', fill: '#fcd34d' }} />
                  <RechartsBar dataKey="quitaPct" radius={[4, 4, 0, 0]}>
                    {confiscatoriedadData.map((entry, index) => (
                      <Cell key={`bar-${index}`} fill={entry.quitaPct > 15 ? '#dc2626' : '#f59e0b'} />
                    ))}
                  </RechartsBar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className="mt-2 text-xs text-rose-100/80">Las barras en rojo intenso superan el umbral jurisprudencial del 15%.</p>
          </div>
        )}

        <div className="overflow-x-auto rounded-xl border border-white/15">
          <table className="min-w-full text-sm">
            <thead className="bg-white/10 text-xs uppercase tracking-wider text-slate-300">
              <tr>
                <th className="px-3 py-2 text-left">Mes/Año</th>
                <th className="px-3 py-2 text-left">Sueldo Original (PDF)</th>
                <th className="px-3 py-2 text-left">Tope Imponible Aplicado</th>
                <th className="px-3 py-2 text-left">Indice Coeficiente</th>
                <th className="px-3 py-2 text-left">Sueldo Actualizado</th>
                <th className="px-3 py-2 text-left">Debug Indice</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10 text-slate-100">
              {filas.map((f) => (
                <tr key={f.id} className={f.excluidoPorCese ? 'bg-rose-500/15' : f.recortadoPorTope ? 'bg-amber-500/15' : ''}>
                  <td className="px-3 py-2">
                    {f.periodo}
                    {f.excluidoPorCese && <span className="ml-2 inline-flex rounded-full border border-rose-300/40 bg-rose-500/20 px-2 py-0.5 text-[11px] text-rose-100">Excluido por Cese</span>}
                    {f.requiereTrazaMoneda && <span title={f.tooltipMoneda} className="ml-2 inline-flex cursor-help rounded-full border border-indigo-300/40 bg-indigo-500/20 px-2 py-0.5 text-[11px] text-indigo-100">Moneda historica</span>}
                  </td>
                  <td className="px-3 py-2">{f.sueldoOriginal.toLocaleString('es-AR')}</td>
                  <td className="px-3 py-2">{f.topeAplicado.toLocaleString('es-AR')}</td>
                  <td className="px-3 py-2">{f.coeficiente.toFixed(6)}</td>
                  <td className="px-3 py-2 font-semibold text-cyan-200">{f.sueldoActualizado.toLocaleString('es-AR')}</td>
                  <td className="px-3 py-2 text-xs">
                    {f.indiceBaseValor > 0 && f.indiceCeseValor > 0 ? `${f.indiceCeseValor.toFixed(4)} / ${f.indiceBaseValor.toFixed(4)} = ${f.coeficiente.toFixed(6)}` : <span className="text-rose-200">Faltante</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    )
  }

  const [tabActiva, setTabActiva] = useState('resumen')
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false)
  const [estrategiaMovilidad, setEstrategiaMovilidad] = useState('conservador')

  // Handler para botón "Descargar Pericia Judicial PDF"
  const handleDescargarPDF = async () => {
    try {
      setIsGeneratingPDF(true)
      
      // Validar que se hayan consumido coins en la sesión actual
      if (!coinsConsumed) {
        alert('Debe realizar un nuevo cálculo para descargar el PDF. Los resultados previos expiraron.')
        setIsGeneratingPDF(false)
        return
      }

      // Confirmación de cierre de sesión
      if (!confirm('Al descargar el PDF se cerrará la sesión de cálculo actual. ¿Desea continuar?')) {
        setIsGeneratingPDF(false)
        return
      }

      // Validar datos esenciales desde el primer escenario calculado
      const primerEscenario = reporteIntegral?.escenarios_calculados?.[0]
      if (!primerEscenario?.haber?.pbu) {
        console.warn('Faltan datos económicos completos (PBU no disponible en escenarios). El PDF se generará con la información disponible.')
      }

      // Extraer datos económicos del primer escenario si están disponibles
      const haberEscenario = primerEscenario?.haber || {}
      const datosEconomicos = {
        promedioRemunerativo: haberEscenario.promedioRemunerativo || economico?.promedioRemunerativo || 0,
        pbu: haberEscenario.pbu || economico?.pbu || 0,
        pc: haberEscenario.pc || economico?.pc || 0,
        pap: haberEscenario.pap || economico?.pap || 0,
        haberTotal: haberEscenario.haberTotal || economico?.haberTotal || 0,
        haberBasico: haberEscenario.haberBasico || economico?.haberBasico || 0,
        tasaSustitucion: haberEscenario.tasaSustitucion || economico?.tasaSustitucion || 0,
        desglose: economico?.desglose || [],
        esConfiscatorio: haberEscenario.esConfiscatorio ?? economico?.esConfiscatorio ?? false
      }

      // Transformar remuneraciones al formato esperado por el PDF
      const remuneracionesFormateadas = (remuneraciones || []).map(r => {
        if (r.periodo && r.remOriginal !== undefined) return r
        const mes = (r.mes !== undefined) ? String(Number(r.mes) + 1).padStart(2, '0') : '01'
        const año = r.año || new Date().getFullYear()
        return {
          periodo: r.periodo || `${mes}/${año}`,
          remOriginal: r.remOriginal || r.remImpSS || r.monto || 0,
          coeficiente: r.coeficiente || 1.0,
          valorFinal: r.valorFinal || r.monto || 0,
          excedeTope: r.excedeTope || false
        }
      })

      const datosCompletos = {
        juridico,
        economico: datosEconomicos,
        escenarios: reporteIntegral?.escenarios_calculados || [],
        historiaLaboral,
        remuneraciones: remuneracionesFormateadas,
        auditoriaCalculo,
        auditoriaIntegridad,
        coinsConsumed: true
      }

      const datosParaPDF = { ...datosCompletos, escenarios: datosCompletos.escenarios }

      // Generar HTML usando la plantilla
      const htmlContent = generarReporteHTML(datosParaPDF)
      
      if (!htmlContent) {
        alert('Error al generar el reporte HTML. Verifique los datos.')
        setIsGeneratingPDF(false)
        return
      }
      
      // Convertir HTML a PDF usando html2canvas y jsPDF
      const { default: jsPDF } = await import('jspdf')
      const doc = new jsPDF({
        format: 'a4',
        orientation: 'portrait',
        unit: 'mm',
        compress: true
      })
      
      // Crear un elemento temporal para renderizar el HTML
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = htmlContent
      tempDiv.style.position = 'absolute'
      tempDiv.style.left = '-9999px'
      tempDiv.style.width = '210mm' // A4 width
      tempDiv.style.height = 'auto'
      tempDiv.style.minHeight = '297mm' // A4 height
      tempDiv.style.fontSize = '12px' // Ensure consistent font size
      document.body.appendChild(tempDiv)
      
      try {
        const canvas = await html2canvas(tempDiv, { 
          scale: 2,
          useCORS: true,
          logging: false
        })
        const imgData = canvas.toDataURL('image/png')
        const imgWidth = doc.internal.pageSize.getWidth()
        const imgHeight = (canvas.height * imgWidth) / canvas.width
        
        doc.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight, undefined, 'FAST')
        
        // Nombre del archivo: Pericia_Judicial_{{APELLIDO}}_{{CUIT}}.pdf
        const apellido = historiaLaboral?.datosPersonales?.apellido || historiaLaboral?.apellido || 'Desconocido'
        const cuil = historiaLaboral?.cuil || historiaLaboral?.datosPersonales?.cuil || 'sin-cuit'
        doc.save(`Pericia_Judicial_${apellido}_${cuil}.pdf`)

        // Resetear la sesión pagada: el usuario recibió su PDF, próximo cálculo debe cobrarse
        window.dispatchEvent(new CustomEvent('sesion-pagada-reset'))
      } finally {
        document.body.removeChild(tempDiv)
        setIsGeneratingPDF(false)
      }
    } catch (error) {
      console.error('Error generando PDF:', error)
      alert('Error al generar el PDF. Por favor intente nuevamente.')
      setIsGeneratingPDF(false)
    }
  }

  if (!resultado) {
    return null
  }

  const {
    juridico = {},
    economico = null,
    remuneraciones = [],
    colorSemafo = 'rojo',
    estadoGeneral = 'nocumple',
    observaciones = [],
    reporteIntegral = null,
    modoAnalisis = 'actual',
    auditoriaCalculo = null
  } = resultado
  const etlConservador =
    resultado?.escenariosEstrategia?.conservador?.etlSummary ||
    resultado?.etlSummaryConservador ||
    resultado?.etlSummary ||
    null
  const etlLitigio =
    resultado?.escenariosEstrategia?.litigio?.etlSummary ||
    resultado?.etlSummaryLitigio ||
    etlConservador
  const etlSummary = estrategiaMovilidad === 'litigio' ? etlLitigio : etlConservador
  const hayDobleEscenario = Boolean(
    resultado?.escenariosEstrategia?.conservador?.etlSummary ||
    resultado?.escenariosEstrategia?.litigio?.etlSummary ||
    resultado?.etlSummaryConservador ||
    resultado?.etlSummaryLitigio
  )
  const retroConservador = Number(etlConservador?.retroactivoEstimado24m || 0)
  const retroLitigio = Number(etlLitigio?.retroactivoEstimado24m || 0)
  const impactoEstrategia = Number((retroLitigio - retroConservador).toFixed(2))

  const descargarRetroCSV = () => {
    const csv = etlSummary?.retroactivoDetalladoCSV
    if (!csv) return
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `retroactivo_detallado_${estrategiaMovilidad}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }
  const auditoriaIntegridad = resultado?.auditoriaIntegridad || { tasaSustitucion: 0, alerta: null }
  const dictamenId = reporteIntegral?.metadata?.fecha_generacion || `DICT-${Date.now()}`
  const versionNormativa = reporteIntegral?.metadata?.version_normativa || reporteIntegral?.metadata?.version_motor || 'desconocida'

  // Determinar color del semáforo
  const colorSemafoConfig = {
    verde: {
      bg: 'bg-green-100',
      border: 'border-green-500',
      text: 'text-green-800',
      icon: CheckCircle,
      mensaje: 'CUMPLE REQUISITOS'
    },
    amarillo: {
      bg: 'bg-yellow-100',
      border: 'border-yellow-500',
      text: 'text-yellow-800',
      icon: AlertTriangle,
      mensaje: 'CUMPLE PARCIALMENTE'
    },
    rojo: {
      bg: 'bg-red-100',
      border: 'border-red-500',
      text: 'text-red-800',
      icon: AlertCircle,
      mensaje: 'NO CUMPLE REQUISITOS'
    }
  }

  const config = colorSemafoConfig[colorSemafo] || colorSemafoConfig.rojo
  const IconComponent = config.icon

  // Timeline de historia laboral
  const periodosHistoria = Array.isArray(historiaLaboral?.periodos)
    ? historiaLaboral.periodos
    : []
  const timelineData = periodosHistoria.map((p, i) => {
    const d1 = toDateSafe(p.fechaDesde)
    const d2 = toDateSafe(p.fechaHasta)
    return {
      id: i,
      nombre: p.empresaNombre || `Período ${i + 1}`,
      tipo: p.tipoActividad,
      desde: d1 ? d1.getFullYear() : 0,
      hasta: d2 ? d2.getFullYear() : 0,
      duracion: `${toLocaleDateStringSafe(d1)} - ${toLocaleDateStringSafe(d2)}`
    }
  })

  // Datos para gráfico de barras (Aportes por caja)
  const cajasPrincipales = juridico?.cajasPrincipales || {}
  const cajas = Object.keys(cajasPrincipales)
  const dataCapas = {
    labels: cajas,
    datasets: [
      {
        label: 'Años de Aportes',
         data: cajas.map(c => cajasPrincipales[c]?.decimal || 0),
        backgroundColor: 'rgba(0, 61, 165, 0.7)',
        borderColor: '#003DA5',
        borderWidth: 2
      }
    ]
  }

  // Datos para gráfico de desglose económico
  const desgloseData = economico ? {
    labels: economico.desglose.map(d => d.concepto.split('(')[0].trim()),
    datasets: [
      {
        label: 'Monto ($)',
        data: economico.desglose.map(d => d.monto),
        backgroundColor: [
          'rgba(0, 61, 165, 0.7)',
          'rgba(255, 215, 0, 0.7)',
          'rgba(76, 175, 80, 0.7)',
          'rgba(200, 200, 200, 0.7)'
        ],
        borderColor: ['#003DA5', '#FFD700', '#4CAF50', '#C8C8C8'],
        borderWidth: 2
      }
    ]
  } : null

  return (
    <div className="bg-white/80 rounded-lg shadow-lg p-6 space-y-8 border border-previsional-border">
      {/* SEMÁFORO DE DERECHO */}
      <div className={`${config.bg} border-l-4 ${config.border} rounded-lg p-6`}>
        <div className="flex items-center gap-4">
          <IconComponent className={`w-12 h-12 ${config.text}`} />
          <div className="flex-1">
           <h3 className={`section-title text-2xl font-bold ${config.text}`}>{config.mensaje}</h3>
            <p className={`text-sm ${config.text} mt-1`}>{juridico.razonRegimen}</p>
          </div>
        </div>

        {/* Detalles rápidos del semáforo */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
           <div className="bg-white/70 rounded p-3 border border-previsional-border">
            <div className="text-xs font-medium text-gray-600">Edad Actual</div>
            <div className="text-lg font-bold text-gray-800">{Math.floor(juridico.edadActual)} años</div>
          </div>
           <div className="bg-white/70 rounded p-3 border border-previsional-border">
            <div className="text-xs font-medium text-gray-600">Requerida</div>
            <div className="text-lg font-bold text-gray-800">{Math.floor(juridico.edadRequerida)} años</div>
          </div>
           <div className="bg-white/70 rounded p-3 border border-previsional-border">
            <div className="text-xs font-medium text-gray-600">Aportes Totales</div>
            <div className="text-lg font-bold text-gray-800">{juridico.aportesTotales.toFixed(2)} años</div>
          </div>
           <div className="bg-white/70 rounded p-3 border border-previsional-border">
            <div className="text-xs font-medium text-gray-600">Régimen</div>
            <div className="text-lg font-bold text-gray-800">{juridico.regimen}</div>
          </div>
        </div>

        {/* Información de compensación 2x1 */}
        {juridico.aplicaCompensacion2x1 && (
          <div className="mt-4 p-3 bg-white bg-opacity-80 rounded border-l-2 border-green-600">
            <div className="text-sm font-semibold text-green-800">✓ Aplica Regla 2x1</div>
            <div className="text-xs text-green-700 mt-1">
              Tienes {juridico.compensacion.diasExcedentes} días de edad excedente, que compensan{' '}
              {juridico.compensacion.diasCompensables} días de aportes faltantes.
            </div>
          </div>
        )}
       </div>

      {etlSummary && (
        <div className="rounded-lg border border-previsional-border bg-[#f8efe4] p-5">
          <div className="flex items-center justify-between gap-4">
            <h3 className="section-title text-xl font-bold text-previsional-dark">
              Escenario de Movilidad Forense
            </h3>
            <span
              className={`rounded-full px-3 py-1 text-xs font-bold ${
                hayDobleEscenario
                  ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                  : 'bg-amber-100 text-amber-800 border border-amber-300'
              } inline-flex items-center gap-1.5`}
            >
              {hayDobleEscenario ? (
                <CheckCircle className="h-3.5 w-3.5" />
              ) : (
                <AlertTriangle className="h-3.5 w-3.5" />
              )}
              {hayDobleEscenario
                ? 'Calculo Forense v20 - Estrategia Dual Activa'
                : 'Calculo Estandar - Modo Unico'}
            </span>
            <span className="rounded-full bg-previsional-accent px-3 py-1 text-xs font-bold text-white">
              Diferencia mensual: ${Number(etlSummary.diferenciaMensual || 0).toFixed(2)}
            </span>
          </div>
          {hayDobleEscenario && (
            <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
              <div className="rounded-lg border border-previsional-border bg-white p-3">
                <p className="text-xs uppercase tracking-wide text-previsional-dark">Retroactivo Conservador</p>
                <p className="text-lg font-bold text-previsional-dark">${retroConservador.toFixed(2)}</p>
              </div>
              <div className="rounded-lg border border-previsional-border bg-[#6E473B] p-3 text-white">
                <p className="text-xs uppercase tracking-wide text-[#f0e4d2]">Retroactivo Litigio</p>
                <p className="text-lg font-bold">${retroLitigio.toFixed(2)}</p>
              </div>
              <div className="rounded-lg border border-previsional-border bg-[#f2e8dc] p-3">
                <p className="text-xs uppercase tracking-wide text-previsional-dark">Impacto Estrategia</p>
                <p className="text-lg font-bold text-previsional-accent">${impactoEstrategia.toFixed(2)}</p>
              </div>
            </div>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              onClick={() => setEstrategiaMovilidad('conservador')}
              disabled={!hayDobleEscenario}
              title={!hayDobleEscenario ? 'Para ver estrategia de litigio, verifique la configuracion del motor.' : ''}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                estrategiaMovilidad === 'conservador'
                  ? 'bg-previsional-dark text-white'
                  : 'bg-white text-previsional-dark border border-previsional-border'
              } ${!hayDobleEscenario ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              Cálculo Conservador
            </button>
            <button
              onClick={() => setEstrategiaMovilidad('litigio')}
              disabled={!hayDobleEscenario}
              title={!hayDobleEscenario ? 'Para ver estrategia de litigio, verifique la configuracion del motor.' : ''}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                estrategiaMovilidad === 'litigio'
                  ? 'bg-[#6E473B] text-white'
                  : 'bg-white text-previsional-dark border border-previsional-border'
              } ${!hayDobleEscenario ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              Estrategia Litigio
            </button>
            {!hayDobleEscenario && (
              <span className="text-xs text-previsional-dark/80">
                Para ver estrategia de litigio, verifique la configuracion del motor.
              </span>
            )}
            {etlSummary?.retroactivoDetalladoCSV && (
              <button
                onClick={descargarRetroCSV}
                className="ml-auto inline-flex items-center gap-2 rounded-md bg-white px-3 py-1.5 text-xs font-semibold text-previsional-dark border border-previsional-border hover:bg-previsional-sand"
              >
                <FileDown className="h-3.5 w-3.5" />
                Descargar Anexo Pericial (CSV)
              </button>
            )}
          </div>
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="rounded-lg border border-previsional-border bg-[#e9ddcf] p-3">
              <p className="text-xs uppercase tracking-wide text-previsional-dark">Escenario ANSES (Ley 24.241)</p>
              <p className="text-2xl font-bold text-previsional-dark">${Number(etlSummary.haberLey24241 || 0).toFixed(2)}</p>
            </div>
            <div className="rounded-lg border border-previsional-border bg-[#6E473B] p-3 text-white">
              <p className="text-xs uppercase tracking-wide text-[#f0e4d2]">Escenario Reajustado (Movilidad)</p>
              <p className="text-2xl font-bold">${Number(etlSummary.haberMovilidad || 0).toFixed(2)}</p>
            </div>
          </div>
          <p className="mt-3 text-xs text-previsional-dark">
            Días base: {etlSummary.totalDiasBase} | Días reconocidos: {etlSummary.totalDiasReconocidos}
          </p>
          {Number(etlSummary.retroactivoEstimado24m || 0) > 0 && (
            <p className="mt-1 text-sm font-semibold text-previsional-accent">
              Retroactivo estimado 24m: ${Number(etlSummary.retroactivoEstimado24m).toFixed(2)}
            </p>
          )}
          {Array.isArray(etlSummary.series) && etlSummary.series.length > 1 && (
            <div className="mt-4 rounded-lg border border-previsional-border bg-white p-3">
              <Line
                data={{
                  labels: etlSummary.series.map((p) => p.periodo),
                  datasets: [
                    {
                      label: 'Haber ANSES',
                      data: etlSummary.series.map((p) => p.anses),
                      borderColor: '#A78D78',
                      backgroundColor: 'rgba(167, 141, 120, 0.2)',
                      fill: false,
                      tension: 0.25
                    },
                    {
                      label: 'Haber Reajustado',
                      data: etlSummary.series.map((p) => p.reajustado),
                      borderColor: '#6E473B',
                      backgroundColor: 'rgba(110, 71, 59, 0.22)',
                      fill: '-1',
                      tension: 0.25
                    }
                  ]
                }}
                options={{
                  responsive: true,
                  plugins: {
                    legend: {position: 'top'}
                  },
                  scales: {
                    x: {ticks: {maxTicksLimit: 10}}
                  }
                }}
              />
            </div>
          )}
          {Array.isArray(etlSummary.retroactivoDetallado) && etlSummary.retroactivoDetallado.length > 1 && (
            <div className="mt-4 rounded-lg border border-previsional-border bg-white p-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-previsional-dark">
                Deuda mensual auditable (24 meses)
              </p>
              <Bar
                data={{
                  labels: etlSummary.retroactivoDetallado.map((p) => p.periodo),
                  datasets: [
                    {
                      label: 'Diferencia mensual',
                      data: etlSummary.retroactivoDetallado.map((p) => p.diferencia),
                      backgroundColor: 'rgba(110, 71, 59, 0.75)',
                      borderColor: '#6E473B',
                      borderWidth: 1.2
                    }
                  ]
                }}
                options={{
                  responsive: true,
                  plugins: { legend: { position: 'top' } },
                  scales: { x: { ticks: { maxTicksLimit: 12 } } }
                }}
              />
            </div>
          )}
        </div>
      )}

       {/* TABLA COMPARATIVA DE ESCENARIOS */}
       {reporteIntegral?.escenarios_calculados && reporteIntegral.escenarios_calculados.length > 1 && (
         <div className="mt-8">
           <h3 className="section-title text-xl font-bold text-previsional-dark mb-4">
             Comparativa de Escenarios Previsonales
           </h3>
            <TablaComparativa
              key={reporteIntegral.metadata?.fecha_generacion || Date.now()}
              escenarios={reporteIntegral.escenarios_calculados}
              cajaOtorgante={juridico?.analisisMulticaja?.cajaOtorgante || juridico?.cajaJubilacion || 'SIPA'}
            />
          </div>
        )}

       {/* ALERTA LEGAL - Fallo Gualtieri y Confiscación */}
      {economico && (
        <AlertaLegal 
          falloGualtieri={economico.falloGualtieri}
          periodosAfectados={economico.periodosAfectados || []}
          tasaSustitucion={economico.tasaSustitucion || 0}
          esConfiscatorio={economico.esConfiscatorio || false}
          haberBasico={economico.haberBasico || 0}
          promedio={economico.promedioRemunerativo || 0}
        />
      )}

      <TicketCreator
        dictamenId={dictamenId}
        versionNormativa={versionNormativa}
      />

        {/* TABS DE INFORMACIÓN */}
        <div>
          <div className="flex gap-2 border-b border-gray-300 mb-6">
            <button
              onClick={() => setTabActiva('resumen')}
              className={`px-4 py-2 font-medium transition ${
                tabActiva === 'resumen'
                  ? 'border-b-2 border-previsional-accent text-previsional-accent'
                  : 'text-gray-600 hover:text-previsional-dark'
              }`}
            >
              Resumen Jurídico
            </button>
            {economico && (
              <button
                onClick={() => setTabActiva('economico')}
                className={`px-4 py-2 font-medium transition ${
                  tabActiva === 'economico'
                    ? 'border-b-2 border-previsional-accent text-previsional-accent'
                    : 'text-gray-600 hover:text-previsional-dark'
                }`}
              >
                Cálculo Económico
              </button>
            )}
            {/* NUEVO: Tab Escenarios para Planificación */}
            {reporteIntegral && modoAnalisis === 'planificacion' && (
              <button
                onClick={() => setTabActiva('escenarios')}
                className={`px-4 py-2 font-medium transition ${
                  tabActiva === 'escenarios'
                    ? 'border-b-2 border-previsional-accent text-previsional-accent'
                    : 'text-gray-600 hover:text-previsional-dark'
                }`}
              >
                📊 Escenarios
              </button>
            )}
            <button
              onClick={() => setTabActiva('timeline')}
              className={`px-4 py-2 font-medium transition ${
                tabActiva === 'timeline'
                  ? 'border-b-2 border-previsional-accent text-previsional-accent'
                  : 'text-gray-600 hover:text-previsional-dark'
              }`}
            >
              Historia Laboral
            </button>
          </div>

        {/* TAB: Resumen Jurídico */}
        {tabActiva === 'resumen' && (
          <div className="space-y-6">
            {/* Desglose por caja */}
             {Object.keys(cajasPrincipales).length > 0 && (
              <div className="bg-[#f6ede2] rounded-lg p-4 border border-previsional-border">
                <h4 className="section-title font-semibold text-previsional-dark mb-4">Aportes por Caja</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(cajasPrincipales).map(([caja, datos]) => (
                    <div key={caja} className="bg-white rounded p-4 border border-previsional-border">
                      <div className="font-medium text-gray-800">{caja}</div>
                      <div className="text-sm text-gray-600 mt-1">
                        {datos.anos} años, {datos.meses} meses, {datos.dias} días
                      </div>
                      <div className="text-lg font-bold text-previsional-accent mt-2">{datos.decimal} años</div>
                      {caja === juridico.cajaJubilacion && (
                        <div className="mt-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded font-semibold">
                          ★ Caja de Jubilación
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Gráfico de aportes por caja */}
                {cajas.length > 0 && (
                  <div className="mt-6">
                    <Bar data={dataCapas} options={{ responsive: true, maintainAspectRatio: true }} />
                  </div>
                )}
              </div>
            )}
 
            {/* Tareas de Cuidado - Only show for female users */}
            {juridico.tareasDesCuidado.totalAños > 0 && 
             (historiaLaboral?.sexo === 'F' || historiaLaboral?.datosPersonales?.sexo === 'F' || 
              juridico?.sexo === 'F' || juridico?.datosPersonales?.sexo === 'F') && (
              <div className="bg-[#efe3d4] rounded-lg p-4 border border-previsional-border">
                <h4 className="section-title font-semibold text-previsional-dark mb-2">Tareas de Cuidado (Reconocidas)</h4>
                <div className="text-sm text-gray-700">
                  Se reconocen <strong>{juridico.tareasDesCuidado.totalAños.toFixed(2)}</strong> años por tareas de
                  cuidado de hijos.
                </div>
              </div>
            )}

            {/* Reciprocidad */}
            {juridico.requiereReciprocidad && (
              <div className="bg-[#efe3d4] rounded-lg p-4 border border-previsional-border">
                <h4 className="section-title font-semibold text-previsional-dark mb-2">Reciprocidad Multi-Caja</h4>
                <div className="text-sm text-gray-700">
                  Te jubilan por la caja <strong>{juridico.cajaJubilacion}</strong>, que es donde acumulaste más años
                  de aportes.
                </div>
              </div>
            )}

            {/* Observaciones */}
            {observaciones && observaciones.length > 0 && (
              <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                <h4 className="font-semibold text-gray-800 mb-3">Observaciones Importantes</h4>
                <ul className="space-y-2">
                  {observaciones.map((obs, i) => (
                    <li key={i} className="text-sm text-gray-700 flex gap-2">
                      <span className="text-yellow-600">•</span>
                      <span>{obs}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* TAB: Cálculo Económico */}
        {tabActiva === 'economico' && economico && (
          <div className="space-y-6">
            {/* Resumen económico */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-[#f1e4d3] rounded-lg p-4 border border-previsional-border">
                <div className="text-sm text-gray-600">Promedio Remunerativo (120 meses)</div>
                <div className="text-3xl font-bold text-previsional-accent mt-1">${economico.promedioRemunerativo.toFixed(2)}</div>
              </div>

              <div className="bg-green-50 rounded-lg p-4 border border-green-600">
                <div className="text-sm text-gray-600">Haber Total Estimado</div>
                <div className="text-3xl font-bold text-green-700 mt-1">${economico.haberTotal.toFixed(2)}</div>
              </div>
            </div>

            {/* Desglose detallado */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-4">Desglose de Prestaciones</h4>
              <div className="space-y-3">
                {economico.desglose.map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-white rounded border border-previsional-border">
                    <div>
                      <div className="font-medium text-gray-800">{item.concepto}</div>
                      <div className="text-xs text-gray-600 mt-1">{item.porcentaje.toFixed(1)}% del total</div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-previsional-accent">${item.monto.toFixed(2)}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Gráfico de desglose */}
              {desgloseData && (
                <div className="mt-6">
                  <Bar data={desgloseData} options={{ responsive: true, maintainAspectRatio: true }} />
                </div>
              )}
            </div>

            {/* Detalles técnicos */}
            <div className="bg-gray-100 rounded-lg p-4 text-xs text-gray-700 space-y-1">
              <div>
                <strong>Promedio Remuneraciones (120 meses reales):</strong> ${economico.promedioRemunerativo.toFixed(2)}
              </div>
              <div>
                <strong>PBU:</strong> ${economico.pbu.toFixed(2)} (Suma Fija Actualizada - Art. 22 Ley 24.241)
              </div>
              <div>
                <strong>PC:</strong> ${economico.pc.toFixed(2)} (1.5% × {economico.anosPre1994} años pre-1994)
              </div>
              <div>
                <strong>PAP:</strong> ${economico.pap.toFixed(2)} (1.5% × {economico.anosPost1994} años post-1994)
              </div>
              <div>
                <strong>Haber Básico:</strong> ${economico.haberBasico.toFixed(2)} (PBU + PC + PAP)
              </div>
            </div>

            {/* Tasa de Sustitución y Alerta Di Pardo */}
            {economico.tasaSustitucion && (
              <div className={`mt-4 p-4 rounded-lg border-l-4 ${economico.esConfiscatorio ? 'bg-red-50 border-red-500' : 'bg-green-50 border-green-500'}`}>
                <div className="flex items-center gap-2">
                  {economico.esConfiscatorio ? (
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  ) : (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  )}
                  <span className="font-bold text-sm">
                    Tasa de Sustitución: {economico.tasaSustitucion}%
                  </span>
                </div>
                {economico.esConfiscatorio && (
                  <div className="mt-2 text-xs text-red-800">
                    <strong>⚠️ DICTAMEN: CONFISCATORIEDAD DETECTADA (Caso Di Pardo)</strong>
                    <p className="mt-1">La tasa es inferior al 70% establecido por jurisprudencia de la Corte Suprema (Fallos "Berman", "Quiroga").</p>
                    <p className="mt-1 font-semibold">Se sugiere demandar por reajuste de topes para alcanzar el 70% (${((economico.haberBasico / 0.70)).toFixed(2)}).</p>
                    <p className="mt-1">Brecha: {economico.tasaSustitucion}% obtenido vs 70% esperado.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* TAB: Escenarios (Planificación Estratégica) */}
        {tabActiva === 'escenarios' && reporteIntegral && (
          <div className="space-y-6">
            {/* Resumen Ejecutivo */}
            <div className="bg-[#efe3d4] border border-previsional-border rounded-lg p-6">
              <h4 className="section-title text-xl font-bold text-previsional-dark mb-2">📊 Análisis Comparativo de Escenarios</h4>
              <p className="text-previsional-dark text-lg">{reporteIntegral.resumen_ejecutivo?.mensaje_principal}</p>
              {reporteIntegral.resumen_ejecutivo?.diferencia_porcentual_beneficio !== 0 && (
                <div className={`mt-3 p-3 rounded-lg ${reporteIntegral.resumen_ejecutivo.diferencia_porcentual_beneficio > 0 ? 'bg-green-100 border border-green-300' : 'bg-red-100 border border-red-300'}`}>
                  <span className="text-sm font-semibold">
                    Diferencia de beneficio: {reporteIntegral.resumen_ejecutivo.diferencia_porcentual_beneficio > 0 ? '+' : ''}{reporteIntegral.resumen_ejecutivo.diferencia_porcentual_beneficio}%
                  </span>
                </div>
              )}
              <p className="text-sm text-previsional-accent mt-3 italic">{reporteIntegral.resumen_ejecutivo?.recomendacion}</p>
            </div>

            {/* Tabla Comparativa de Decisiones */}
            <div className="bg-white border border-previsional-border rounded-lg overflow-hidden">
              <h5 className="section-title font-semibold text-previsional-dark p-4 bg-[#f8efe4] border-b border-previsional-border">Tabla Comparativa de Escenarios</h5>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-previsional-dark text-previsional-bg">
                    <tr>
                      <th className="px-4 py-3 text-left">Escenario</th>
                      <th className="px-4 py-3 text-right">Haber Mensual</th>
                      <th className="px-4 py-3 text-right">Tasa Sustitución</th>
                      <th className="px-4 py-3 text-center">Edad al Cese</th>
                      <th className="px-4 py-3 text-center">Confiscatorio</th>
                    </tr>
                  </thead>
                  <tbody>
                      {reporteIntegral.escenarios_calculados?.map((esc, idx) => {
                        const tasaVal = esc.haber?.tasaSustitucion ?? esc.diagnostico?.tasaSustitucion ?? 0
                        const esBaja = esc.diagnostico?.esBaja ?? esc.haber?.esConfiscatorio ?? false
                        return (
                        <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-[#fcf6ee]'}>
                          <td className="px-4 py-3 font-medium text-gray-800">
                            {esc.nombre}
                            <div className="text-xs text-gray-500">{esc.descripcion || ''}</div>
                          </td>
                          <td className="px-4 py-3 text-right font-bold text-previsional-accent">
                            ${esc.haber_mensual?.toFixed(2) || '0.00'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className={`font-semibold ${esBaja ? 'text-red-600' : 'text-green-600'}`}>
                              {tasaVal.toFixed(2)}%
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center text-gray-700">
                            {esc.edad_cese} años
                          </td>
                          <td className="px-4 py-3 text-center">
                            {esBaja ? (
                              <span className="inline-flex items-center gap-1 text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                                <AlertTriangle className="w-3 h-3" /> Sí
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                <CheckCircle className="w-3 h-3" /> No
                              </span>
                            )}
                          </td>
                        </tr>
                      )})}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Detalle de cada escenario */}
            <div className="space-y-4">
              <h5 className="section-title font-semibold text-previsional-dark">Detalle de Escenarios</h5>
              {reporteIntegral.escenarios_calculados?.map((esc, idx) => {
                const tasaVal = esc.haber?.tasaSustitucion ?? esc.diagnostico?.tasaSustitucion ?? 0
                const esBaja = esc.diagnostico?.esBaja ?? esc.haber?.esConfiscatorio ?? false
                return (
                <div key={idx} className="bg-[#f8efe4] rounded-lg p-4 border border-previsional-border">
                  <div className="flex items-center justify-between mb-3">
                    <h6 className="font-bold text-previsional-dark">{esc.nombre}</h6>
                    <span className="text-2xl font-bold text-previsional-accent">${esc.haber_mensual?.toFixed(2)}</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <span className="text-gray-600">Fecha Cese:</span>
                      <div className="font-medium">{esc.fecha_cese || 'N/A'}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">Edad:</span>
                      <div className="font-medium">{esc.edad_cese} años</div>
                    </div>
                    <div>
                      <span className="text-gray-600">Tasa Sustitución:</span>
                      <div className={`font-medium ${esBaja ? 'text-red-600' : 'text-green-600'}`}>
                        {tasaVal.toFixed(2)}%
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-600">Régimen:</span>
                      <div className="font-medium">{esc.regimen || 'N/A'}</div>
                    </div>
                  </div>
                  {esBaja && (
                    <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800">
                      ⚠️ Tasa de sustitución ({tasaVal.toFixed(2)}%) por debajo del umbral constitucional (70%).
                    </div>
                  )}
                </div>
              )})}
            </div>

            {/* Firma Técnica */}
            <div className="p-4 bg-gray-50 border-t border-gray-200 rounded-lg">
              <div className="text-xs text-gray-600">
                <strong>Responsable:</strong> {reporteIntegral.firma_tecnica?.responsable}<br />
                <strong>Versión Canon:</strong> {reporteIntegral.firma_tecnica?.version_canon}<br />
                <strong>Advertencia:</strong> {reporteIntegral.firma_tecnica?.advertencia}
              </div>
            </div>
          </div>
        )}

        {/* TAB: Timeline */}
        {tabActiva === 'timeline' && (
          <div className="space-y-4">
            <div className="text-sm text-gray-600 mb-4">
              Total de períodos: <strong>{timelineData.length}</strong>
            </div>

            <div className="space-y-3">
              {timelineData.map((periodo, i) => (
                <div key={periodo.id} className="flex gap-4">
                  {/* Línea del timeline */}
                  <div className="flex flex-col items-center">
                    <div className="w-3 h-3 rounded-full bg-previsional-accent border-2 border-white"></div>
                    {i < timelineData.length - 1 && <div className="w-0.5 h-12 bg-gray-300"></div>}
                  </div>

                  {/* Contenido del período */}
                  <div className="pb-4 flex-1">
                     <div className="bg-[#f8efe4] rounded-lg p-3 border border-previsional-border">
                      <div className="font-medium text-gray-800">{periodo.nombre}</div>
                      <div className="text-sm text-gray-600 mt-1">{periodo.duracion}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        Tipo: <strong>{periodo.tipo}</strong> | {periodo.desde}-{periodo.hasta}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 text-white shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-400">Tasa de Sustitutividad</p>
            <h3 className="text-2xl font-bold">{Number(auditoriaIntegridad.tasaSustitucion || 0).toFixed(2)}%</h3>
          </div>
          <div className={`rounded-full border px-3 py-1 text-xs font-bold ${
            !auditoriaIntegridad.alerta
              ? 'border-green-500/50 bg-green-500/20 text-green-400'
              : auditoriaIntegridad.alerta.nivel === 'critico'
                ? 'border-red-500/50 bg-red-500/20 text-red-400'
                : 'border-amber-500/50 bg-amber-500/20 text-amber-400'
          }`}>
            {auditoriaIntegridad.alerta ? '⚠ REQUIERE REVISION' : '✅ CONSISTENTE'}
          </div>
        </div>
        {auditoriaIntegridad.alerta && (
          <p className="mt-2 text-xs italic text-slate-300">{auditoriaIntegridad.alerta.mensaje}</p>
        )}
      </div>

      {/* Botón de Descarga PDF Judicial */}
      {resultado.reporteIntegral?.escenarios_calculados?.length > 0 && (
        <div className="flex justify-end">
            <button
              onClick={handleDescargarPDF}
              disabled={isGeneratingPDF}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition ${
                isGeneratingPDF
                  ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                  : 'bg-red-600 text-white hover:bg-red-700'
              }`}
            >
              <FileDown className="w-5 h-5" />
              {isGeneratingPDF ? 'Generando...' : 'Descargar Pericia Judicial PDF'}
            </button>
        </div>
      )}

      {/* Disclaimer Legal */}
      <div className="p-4 bg-[#efe3d4] border border-previsional-border rounded-lg">
        <p className="text-xs text-previsional-dark italic">
          ⚖️ <strong>Disclaimer Legal:</strong> Este simulador proporciona estimaciones basadas en la normativa
          argentina 2026. No constituye asesoramiento legal ni reemplazo de consulta profesional. Consulte con un
          abogado especialista en derecho previsional para validar su situación específica.
        </p>
      </div>
    </div>
  )
}
